import { useEffect, useRef, useState } from 'react';
import { useLocale } from '../../lib/i18n/useLocale';
import { SUPPORTED_LOCALES, LOCALE_LABELS, type Locale } from '../../lib/i18n/locales';

interface LanguageSwitcherProps {
  className?: string;
  /** 드롭다운이 위로 열릴지 여부 (기본 아래) */
  dropUp?: boolean;
}

export function LanguageSwitcher({ className = '', dropUp = false }: LanguageSwitcherProps) {
  const { locale, setLocale, t } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const current = LOCALE_LABELS[locale];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(loc: Locale) {
    setLocale(loc);
    setIsOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') setIsOpen(false);
  }

  return (
    <div ref={containerRef} className={`relative inline-block ${className}`} onKeyDown={handleKeyDown}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('common.languageSwitch')}
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-control border border-ink-300 bg-white hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-brand-500 transition-colors"
      >
        <span aria-hidden="true">{current.flag}</span>
        <span className="font-medium">{current.native}</span>
        <svg
          className={`w-4 h-4 text-ink-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label={t('common.languageSwitch')}
          className={`absolute z-50 min-w-[160px] bg-white border border-ink-200 rounded-card shadow-lg overflow-y-auto max-h-64 ${
            dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
          } right-0`}
        >
          {SUPPORTED_LOCALES.map((loc) => {
            const label = LOCALE_LABELS[loc];
            const selected = loc === locale;
            return (
              <li
                key={loc}
                role="option"
                aria-selected={selected}
                onClick={() => handleSelect(loc)}
                className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors ${
                  selected
                    ? 'bg-brand-50 text-brand-700 font-medium'
                    : 'text-ink-700 hover:bg-ink-50'
                }`}
              >
                <span aria-hidden="true">{label.flag}</span>
                <span>{label.native}</span>
                {selected && (
                  <svg
                    className="ml-auto w-4 h-4 text-brand-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
